import { useEffect, useRef, useState, type RefObject } from "react";
import { NAV, type NavItem } from "@/config/site";
import { useScrolled } from "@/hooks/useMotion";
import { cn } from "@/lib/cn";
import { Link, NavLink, useLocation } from "@/router/Router";
import { useAuth } from "@/state/AuthProvider";
import { useIsLive } from "@/state/LiveProvider";
import { useMessaging } from "@/state/MessagingProvider";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { Logo } from "@/components/ui/Logo";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import { useTheme } from "@/state/ThemeProvider";
import { MobileMenu } from "./MobileMenu";

/** Bright theme: the brand-green surfaces carry dark ink, so only these count as dark for the header (see styles/tokens.css). */
const DARK_SURFACES_LIGHT_THEME = ".tone-dark, .hero, .page-hero:not(.page-hero--evergreen), .pc-hero";

/**
 * Whether the header sits over a dark surface right now. Over dark, the transparent header keeps
 * light text; over a light surface (bright theme, pages without a dark hero) it turns solid.
 */
function useOverDark(headerRef: RefObject<HTMLElement | null>, deps: unknown[]): boolean {
  const { theme } = useTheme();
  const [overDark, setOverDark] = useState(true);
  useEffect(() => {
    if (theme === "dark") {
      setOverDark(true);
      return;
    }
    let frame = 0;
    const check = () => {
      frame = 0;
      const header = headerRef.current;
      const y = (header?.offsetHeight ?? 72) + 4;
      const under = document
        .elementsFromPoint(window.innerWidth / 2, y)
        .find((el) => !header?.contains(el) && !el.closest(".fab-dock, .ai-panel, .audio-dock, .nav-panel"));
      setOverDark(Boolean(under?.closest(DARK_SURFACES_LIGHT_THEME)));
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(check);
    };
    const timers = [0, 120, 400, 1000, 2000].map((t) => window.setTimeout(schedule, t));
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      timers.forEach(window.clearTimeout);
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, ...deps]);
  return overDark;
}

/**
 * Transparent over the hero; becomes a blurred evergreen bar after scrolling. Desktop sections
 * open as disclosure panels (hover, click or keyboard). Below 1080px a full-screen menu takes over.
 */
export function Header() {
  const { user } = useAuth();
  const scrolled = useScrolled(32);
  const { pathname } = useLocation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const isLive = useIsLive();
  const closeTimer = useRef<number | undefined>(undefined);
  const navRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const overDark = useOverDark(headerRef, [pathname]);

  useEffect(() => {
    setOpenIndex(null);
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (openIndex === null) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpenIndex(null);
    const onDown = (e: PointerEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setOpenIndex(null);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [openIndex]);

  const enter = (i: number) => {
    window.clearTimeout(closeTimer.current);
    setOpenIndex(i);
  };
  const leave = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpenIndex(null), 180);
  };

  return (
    <>
      <header ref={headerRef} className={cn("site-header", (scrolled || openIndex !== null || !overDark) && "is-solid", overDark && "over-dark")}>
        <div className="site-header__inner">
          <Link to={user ? "/welcome" : "/"} className="site-header__brand" aria-label="Miracle Assembly — home">
            <Logo />
          </Link>

          <nav ref={navRef} className="site-nav" aria-label="Main">
            <ul className="site-nav__list">
              {NAV.map((item, i) => (
                <li key={item.label} className="site-nav__item" onMouseEnter={() => item.children && enter(i)} onMouseLeave={() => item.children && leave()}>
                  {item.children ? (
                    <>
                      <button
                        type="button"
                        className={cn("site-nav__trigger", (pathname === item.href || pathname.startsWith(`${item.href}/`)) && "is-active")}
                        aria-expanded={openIndex === i}
                        aria-controls={`nav-panel-${i}`}
                        onClick={() => setOpenIndex(openIndex === i ? null : i)}
                      >
                        {item.label}
                        <Icon name="chevronDown" size={14} />
                      </button>
                      <NavPanel item={item} id={`nav-panel-${i}`} open={openIndex === i} />
                    </>
                  ) : (
                    <NavLink to={item.href} section className="site-nav__trigger">
                      {item.label}
                    </NavLink>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          <div className="site-header__actions">
            <Link to="/watch/live" className={cn("live-chip", isLive && "is-live")} aria-label={isLive ? "We are live — watch now" : "Watch live"}>
              <span className="live-dot" aria-hidden />
              <span>{isLive ? "Live now" : "Watch"}</span>
            </Link>
            <MemberLink />
            <ThemeToggle className="site-header__theme" />
            <Button to="/account" variant="flame" size="sm" className={cn("site-header__give", user && "site-header__give--signed-in")}>
              {user ? "Member dashboard" : "Sign in"}
            </Button>
            <button type="button" className="menu-toggle" aria-label="Open menu" aria-expanded={menuOpen} aria-controls="mobile-menu" onClick={() => setMenuOpen(true)}>
              <span className="menu-toggle__label">Menu</span>
              <span className="menu-toggle__bars" aria-hidden>
                <span />
                <span />
              </span>
            </button>
          </div>
        </div>
      </header>
      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
}

/** Signed-in members: messages with an unread count. Signed out: nothing extra in the bar. */
function MemberLink() {
  const { user } = useAuth();
  const { unreadTotal, enabled } = useMessaging();
  if (!user) return null;
  const label = enabled ? (unreadTotal ? `Messages, ${unreadTotal} unread` : "Messages") : "Your account";
  return (
    <Link to={enabled ? "/messages" : "/account"} className="header-member" aria-label={label} title={label}>
      <Icon name={enabled ? "message" : "user"} size={20} />
      {enabled && unreadTotal > 0 ? (
        <span className="header-member__badge" aria-hidden>
          {unreadTotal > 99 ? "99+" : unreadTotal}
        </span>
      ) : null}
    </Link>
  );
}

function NavPanel({ item, id, open }: { item: NavItem; id: string; open: boolean }) {
  const children = item.children ?? [];
  return (
    <div id={id} className={cn("nav-panel", open && "is-open")} hidden={!open}>
      <div className="nav-panel__inner">
        <div className="nav-panel__intro">
          <p className="eyebrow eyebrow--flame">{item.label}</p>
          <p className="nav-panel__title">{PANEL_COPY[item.label] ?? item.label}</p>
          <Link to={item.href} className="text-link">
            Explore {item.label.toLowerCase()} <Icon name="arrowRight" size={16} />
          </Link>
        </div>
        <ul className={cn("nav-panel__links", children.length > 6 && "nav-panel__links--3col")}>
          {children.map((child) => (
            <li key={child.href + child.label}>
              <Link to={child.href} className="nav-panel__link">
                <span className="nav-panel__link-label">{child.label}</span>
                {child.description ? <span className="nav-panel__link-desc">{child.description}</span> : null}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const PANEL_COPY: Record<string, string> = {
  About: "A Spirit & Word church, liberating people for quality living.",
  Connect: "Find your place in the Quality Living Tribe.",
  Expressions: "Communities for every season of life and leadership.",
  Watch: "Services, messages and declarations — wherever you are.",
};
